let petraloDiscountActionConfigured = false;

const petraloFormatCartMoney = (amount, currencyCode = "USD") => {
  const numericAmount = Number(amount);
  const hasCents = Math.round(numericAmount * 100) % 100 !== 0;
  return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(numericAmount);
};

const petraloSetDiscountFeedback = (
  wrapper,
  { message = "", state = "", removable = false, subtotal = "", discount = "" } = {}
) => {
  const status = wrapper?.querySelector("[data-petralo-discount-status]");
  const messageElement = wrapper?.querySelector("[data-petralo-discount-message]");
  const removeButton = wrapper?.querySelector("[data-petralo-discount-remove]");
  const summary = wrapper?.querySelector("[data-petralo-discount-summary]");
  const subtotalElement = wrapper?.querySelector("[data-petralo-discount-subtotal]");
  const discountElement = wrapper?.querySelector("[data-petralo-discount-amount]");

  wrapper?.classList.toggle("is-applied", state === "applied");
  wrapper?.classList.toggle("is-error", state === "error");
  if (status) status.hidden = !message;
  if (messageElement) messageElement.textContent = message;
  if (removeButton) removeButton.hidden = !removable;
  if (summary) summary.hidden = !(subtotal && discount);
  if (subtotalElement) subtotalElement.textContent = subtotal;
  if (discountElement) discountElement.textContent = discount;
};

const petraloRenderDiscountedCart = (cart, code = "") => {
  const totalElement = document.querySelector("#MinimogCartDrawer [data-petralo-cart-total]");
  const totalAmount = cart?.cost?.totalAmount;
  if (totalElement && totalAmount) {
    totalElement.textContent = petraloFormatCartMoney(totalAmount.amount, totalAmount.currencyCode);
  }

  const pageTotals = document.querySelector("#MinimogCart [data-cart-subtotal]");
  const pageSubtotal = pageTotals?.querySelector("[data-cart-subtotal-price]");
  const subtotalAmount = cart?.cost?.subtotalAmount;
  if (pageSubtotal && totalAmount) {
    pageSubtotal.textContent = petraloFormatCartMoney(totalAmount.amount, totalAmount.currencyCode);
  }

  if (pageTotals && subtotalAmount && totalAmount) {
    const discountValue = Math.max(0, Number(subtotalAmount.amount) - Number(totalAmount.amount));
    let discountRow = pageTotals.querySelector("[data-petralo-cart-discount-total]");

    if (discountValue > 0) {
      if (!discountRow) {
        discountRow = document.createElement("div");
        discountRow.className = "petralo-cart-page-discount-total m:flex m:justify-between";
        discountRow.dataset.petraloCartDiscountTotal = "";
        discountRow.innerHTML = "<span>Discount</span><span></span>";
        pageTotals.append(discountRow);
      }
      discountRow.lastElementChild.textContent = `- ${petraloFormatCartMoney(
        discountValue,
        totalAmount.currencyCode
      )}`;
    } else {
      discountRow?.remove();
    }
  }
};

const petraloRefreshCartItems = async () => {
  const cartUrl = window.MinimogSettings?.routes?.cart || "/cart";
  const response = await fetch(`${cartUrl}?section_id=cart-drawer`, {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) throw new Error("Unable to refresh cart items.");

  const sectionHtml = await response.text();
  const parsedSection = new DOMParser().parseFromString(sectionHtml, "text/html");
  const currentItems = document.querySelector("#MinimogCartDrawer [data-minimog-cart-items]");
  const updatedItems = parsedSection.querySelector("[data-minimog-cart-items]");
  if (currentItems && updatedItems) currentItems.innerHTML = updatedItems.innerHTML;
};

const petraloRecoverCartState = async () => {
  await petraloRefreshCartItems();

  const rootUrl = window.Shopify?.routes?.root || "/";
  const response = await fetch(`${rootUrl}cart.js`, {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) throw new Error("Unable to recover the current cart state.");

  const cart = await response.json();
  const drawer = document.querySelector("#MinimogCartDrawer");
  const totalElement = drawer?.querySelector("[data-petralo-cart-total]");
  const currencyCode = cart.currency || "USD";
  if (totalElement) {
    totalElement.textContent = petraloFormatCartMoney(cart.total_price / 100, currencyCode);
    totalElement.dataset.cartTotalPrice = cart.total_price;
  }

  drawer?.classList.toggle("m-cart--empty", cart.item_count === 0);
  document.querySelectorAll(".m-cart-count-bubble").forEach((count) => {
    count.textContent = cart.item_count;
    count.classList.toggle("m:hidden", cart.item_count === 0);
  });

  const savedDiscountCode = localStorage.getItem("minimog-discount-code");
  const discountWrapper = drawer?.querySelector("[data-petralo-discount]");
  if (savedDiscountCode && discountWrapper && cart.total_discount > 0) {
    const input = discountWrapper.querySelector("[data-petralo-discount-input]");
    const applyButton = discountWrapper.querySelector("[data-petralo-discount-apply]");
    if (input) input.value = savedDiscountCode;
    if (applyButton) applyButton.textContent = "Applied";
    petraloSetDiscountFeedback(discountWrapper, {
      message: savedDiscountCode.toUpperCase(),
      state: "applied",
      removable: true,
      subtotal: petraloFormatCartMoney(cart.original_total_price / 100, currencyCode),
      discount: `- ${petraloFormatCartMoney(cart.total_discount / 100, currencyCode)}`,
    });
  }
};

const petraloGetDiscountAction = () => {
  const updateCart = globalThis.Shopify?.actions?.updateCart;
  if (typeof updateCart !== "function") return null;

  if (!petraloDiscountActionConfigured && typeof updateCart.configure === "function") {
    updateCart.configure({
      eventTarget: () => document.querySelector("#MinimogCartDrawer"),
      async handler(defaultHandler) {
        return defaultHandler();
      },
    });
    petraloDiscountActionConfigured = true;
  }
  return updateCart;
};

const petraloApplyDiscountCode = async (code, wrapper, { silent = false } = {}) => {
  const applyButton = wrapper?.querySelector("[data-petralo-discount-apply]");
  const normalizedCode = code.trim();
  if (!normalizedCode) return false;

  wrapper?.classList.add("is-loading");
  if (applyButton) {
    applyButton.disabled = true;
    applyButton.textContent = "Applying…";
  }
  if (!silent) petraloSetDiscountFeedback(wrapper);

  try {
    const updateCart = petraloGetDiscountAction();
    if (updateCart) {
      const result = await updateCart({ discountCodes: [normalizedCode] }, { event: { context: "cart" } });
      if (result.userErrors?.length) throw new Error(result.userErrors[0].message);

      const appliedCode = result.cart?.discountCodes?.find(
        (discount) => discount.code.toLowerCase() === normalizedCode.toLowerCase()
      );
      if (!appliedCode?.applicable) {
        petraloRenderDiscountedCart(result.cart);
        await petraloRefreshCartItems();
        throw new Error("This discount code is not valid for the items in your cart.");
      }

      petraloRenderDiscountedCart(result.cart, normalizedCode);
      await petraloRefreshCartItems();
      localStorage.setItem("minimog-discount-code", normalizedCode);
      const originalLineTotal = Array.from(
        document.querySelectorAll("#MinimogCartDrawer .m-cart-drawer-item[data-line-original-price]")
      ).reduce((total, row) => total + Number(row.dataset.lineOriginalPrice || 0), 0);
      const originalTotal = originalLineTotal > 0
        ? originalLineTotal / 100
        : Number(document.querySelector("#MinimogCartDrawer [data-petralo-cart-total]")?.dataset.cartTotalPrice || 0) / 100;
      const discountedTotal = Number(result.cart?.cost?.totalAmount?.amount || originalTotal);
      const currencyCode = result.cart?.cost?.totalAmount?.currencyCode || "USD";
      const savings = Math.max(0, originalTotal - discountedTotal);
      const displayCode = normalizedCode.toUpperCase();
      petraloSetDiscountFeedback(wrapper, {
        message: displayCode,
        state: "applied",
        removable: true,
        subtotal: savings > 0 ? petraloFormatCartMoney(originalTotal, currencyCode) : "",
        discount: savings > 0 ? `- ${petraloFormatCartMoney(savings, currencyCode)}` : "",
      });
    } else {
      const discountUrl = `${window.Shopify?.routes?.root || "/"}discount/${encodeURIComponent(normalizedCode)}?redirect=${encodeURIComponent(window.location.pathname)}`;
      const response = await fetch(discountUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error("Unable to apply this discount code.");

      localStorage.setItem("minimog-discount-code", normalizedCode);
      petraloSetDiscountFeedback(wrapper, {
        message: normalizedCode.toUpperCase(),
        state: "applied",
      });
    }

    if (applyButton) applyButton.textContent = "Applied";
    return true;
  } catch (error) {
    localStorage.removeItem("minimog-discount-code");
    petraloSetDiscountFeedback(wrapper, {
      message: error.message || "Unable to apply this discount code.",
      state: "error",
    });
    if (applyButton) applyButton.textContent = "Apply";
    return false;
  } finally {
    wrapper?.classList.remove("is-loading");
    if (applyButton) applyButton.disabled = false;
  }
};

const petraloRemoveDiscountCode = async (wrapper) => {
  const updateCart = petraloGetDiscountAction();
  if (updateCart) {
    const result = await updateCart({ discountCodes: [] }, { event: { context: "cart" } });
    if (result.userErrors?.length) throw new Error(result.userErrors[0].message);
    petraloRenderDiscountedCart(result.cart);
    await petraloRefreshCartItems();
  }

  localStorage.removeItem("minimog-discount-code");
  const input = wrapper?.querySelector("[data-petralo-discount-input]");
  const applyButton = wrapper?.querySelector("[data-petralo-discount-apply]");
  if (input) input.value = "";
  if (applyButton) applyButton.textContent = "Apply";
  petraloSetDiscountFeedback(wrapper);
};

document.addEventListener("click", (event) => {
  const orderNotesButton = event.target.closest("[data-petralo-order-notes]");
  if (orderNotesButton) {
    const nativeNoteButton = document.querySelector(
      "#MinimogCartDrawer .m-cart-addon--trigger-button[data-open='note']"
    );
    nativeNoteButton?.click();
    return;
  }

  const discountRemoveButton = event.target.closest("[data-petralo-discount-remove]");
  if (discountRemoveButton) {
    petraloRemoveDiscountCode(discountRemoveButton.closest("[data-petralo-discount]")).catch((error) => {
      petraloSetDiscountFeedback(discountRemoveButton.closest("[data-petralo-discount]"), {
        message: error.message || "Unable to remove this discount code.",
        state: "error",
      });
    });
    return;
  }

  const discountApplyButton = event.target.closest("[data-petralo-discount-apply]");
  if (!discountApplyButton) return;

  const discountWrapper = discountApplyButton.closest("[data-petralo-discount]");
  const discountInput = discountWrapper?.querySelector("[data-petralo-discount-input]");
  const discountCode = discountInput?.value.trim();

  if (!discountCode) {
    discountInput?.focus();
    petraloSetDiscountFeedback(discountWrapper, { message: "Enter a discount code.", state: "error" });
    return;
  }

  petraloApplyDiscountCode(discountCode, discountWrapper);
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Enter" || !event.target.matches("[data-petralo-discount-input]")) return;
  event.preventDefault();
  event.target.closest("[data-petralo-discount]")?.querySelector("[data-petralo-discount-apply]")?.click();
});

document.addEventListener("input", (event) => {
  if (!event.target.matches("[data-petralo-discount-input]")) return;

  const discountWrapper = event.target.closest("[data-petralo-discount]");
  const discountApplyButton = discountWrapper?.querySelector("[data-petralo-discount-apply]");

  if (discountApplyButton) discountApplyButton.textContent = "Apply";
  petraloSetDiscountFeedback(discountWrapper);
  if (!event.target.value.trim()) localStorage.removeItem("minimog-discount-code");
});

const petraloCartUpdateTimers = new Map();
let petraloCartUpdateQueue = Promise.resolve();
let petraloCartPendingUpdates = 0;

const petraloSetCartBusy = (busy) => {
  const drawer = document.querySelector("#MinimogCartDrawer");
  const checkoutButton = drawer?.querySelector('[name="checkout"]');

  if (busy) {
    drawer?.setAttribute("aria-busy", "true");
    if (checkoutButton) {
      if (!checkoutButton.hasAttribute("data-petralo-was-disabled")) {
        checkoutButton.dataset.petraloWasDisabled = checkoutButton.disabled ? "true" : "false";
      }
      checkoutButton.disabled = true;
      checkoutButton.setAttribute("aria-disabled", "true");
    }
    return;
  }

  drawer?.removeAttribute("aria-busy");
  if (checkoutButton) {
    if (checkoutButton.dataset.petraloWasDisabled !== "true") checkoutButton.disabled = false;
    delete checkoutButton.dataset.petraloWasDisabled;
    checkoutButton.removeAttribute("aria-disabled");
  }
};

const petraloRenderCartState = (state) => {
  const sectionHtml = state.sections?.["cart-drawer"];
  if (!sectionHtml) throw new Error("Cart drawer section was not returned.");

  const parsedSection = new DOMParser().parseFromString(sectionHtml, "text/html");
  const selectors = [
    "[data-minimog-cart-items]",
    "[data-minimog-cart-discounts]",
    "[data-cart-subtotal]",
    "[data-petralo-cart-total]",
    "[data-minimog-gift-wrapping]",
  ];

  selectors.forEach((selector) => {
    const currentElement = document.querySelector(`#MinimogCartDrawer ${selector}`);
    const updatedElement = parsedSection.querySelector(selector);
    if (currentElement && updatedElement) {
      currentElement.innerHTML = updatedElement.innerHTML;
      if (selector === "[data-petralo-cart-total]") {
        currentElement.dataset.cartTotalPrice = updatedElement.dataset.cartTotalPrice;
      }
    }
  });

  document.querySelector("#MinimogCartDrawer")?.classList.toggle("m-cart--empty", state.item_count === 0);
  document.querySelectorAll(".m-cart-count-bubble").forEach((count) => {
    count.textContent = state.item_count;
    count.classList.toggle("m:hidden", state.item_count === 0);
  });
};

const petraloUpdateCartLine = async (line, quantity) => {
  petraloSetCartBusy(true);
  document.getElementById(`MinimogCartDrawer-Item-${line}`)?.classList.add("is-updating");

  try {
    const response = await fetch(window.MinimogSettings.routes.cart_change_url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        line,
        quantity,
        sections: ["cart-drawer"],
        sections_url: window.location.pathname,
      }),
    });
    const state = await response.json();
    if (!response.ok || state.errors) throw new Error(state.description || state.errors || "Cart update failed.");
    petraloRenderCartState(state);

    const savedDiscountCode = localStorage.getItem("minimog-discount-code");
    const discountWrapper = document.querySelector("#MinimogCartDrawer [data-petralo-discount]");
    if (savedDiscountCode && discountWrapper && petraloGetDiscountAction()) {
      await petraloApplyDiscountCode(savedDiscountCode, discountWrapper, { silent: true });
    }
  } catch (error) {
    console.error("PETRALO cart update error:", error);
    try {
      await petraloRecoverCartState();
    } catch (recoveryError) {
      console.error("PETRALO cart recovery error:", recoveryError);
      window.location.reload();
    }
  } finally {
    petraloCartPendingUpdates = Math.max(0, petraloCartPendingUpdates - 1);
    if (petraloCartPendingUpdates === 0) {
      petraloSetCartBusy(false);
    }
  }
};

const petraloQueueCartUpdate = (line, quantity) => {
  petraloSetCartBusy(true);
  document.getElementById(`MinimogCartDrawer-Item-${line}`)?.classList.add("is-updating");

  const existingTimer = petraloCartUpdateTimers.get(line);
  window.clearTimeout(existingTimer);
  if (!existingTimer) petraloCartPendingUpdates += 1;
  const timer = window.setTimeout(() => {
    petraloCartUpdateTimers.delete(line);
    petraloCartUpdateQueue = petraloCartUpdateQueue.then(() => petraloUpdateCartLine(line, quantity));
  }, 250);
  petraloCartUpdateTimers.set(line, timer);
};

document.addEventListener(
  "change",
  (event) => {
    const quantityInput = event.target.closest("#MinimogCartDrawer .m-quantity__input[data-index]");
    if (!quantityInput) return;

    event.stopPropagation();
    petraloQueueCartUpdate(Number(quantityInput.dataset.index), Number(quantityInput.value));
  },
  true
);

document.addEventListener(
  "click",
  (event) => {
    const removeButton = event.target.closest("#MinimogCartDrawer m-cart-remove-button[data-index]");
    if (!removeButton) return;

    event.preventDefault();
    event.stopPropagation();
    petraloQueueCartUpdate(Number(removeButton.dataset.index), 0);
  },
  true
);

if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
  window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, () => {
    const savedDiscountCode = localStorage.getItem("minimog-discount-code");
    if (!savedDiscountCode) return;

    window.setTimeout(() => {
      const discountWrapper = document.querySelector("#MinimogCartDrawer [data-petralo-discount]");
      const discountInput = discountWrapper?.querySelector("[data-petralo-discount-input]");
      if (!discountWrapper || !petraloGetDiscountAction()) return;

      if (discountInput) discountInput.value = savedDiscountCode;
      petraloApplyDiscountCode(savedDiscountCode, discountWrapper, { silent: true });
    }, 50);
  });
}

const petraloRestoreSavedDiscount = () => {
  const savedDiscountCode = localStorage.getItem("minimog-discount-code");
  const discountWrapper = document.querySelector("#MinimogCartDrawer [data-petralo-discount]");
  const discountInput = discountWrapper?.querySelector("[data-petralo-discount-input]");
  if (!savedDiscountCode || !discountWrapper || !petraloGetDiscountAction()) return;

  if (discountInput) discountInput.value = savedDiscountCode;
  petraloApplyDiscountCode(savedDiscountCode, discountWrapper, { silent: true });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => window.setTimeout(petraloRestoreSavedDiscount, 50), {
    once: true,
  });
} else {
  window.setTimeout(petraloRestoreSavedDiscount, 50);
}
